import { useCallback, useEffect, useRef, useState } from 'react'
import AsyncSelect from 'react-select/async'
import { FilterSelectStyle } from '../BasicFilter/FilterSelectStyle'
import { ActionMeta, MultiValue } from 'react-select'
import { OptionType } from '@/app/utils/optionSelectType'
import debounce from 'lodash.debounce'

interface BaseParams {
  search?: string
  limit?: number
  skip?: number
  sort?: string
}

interface BasicAsyncSelectProps {
  handleSelectedValue: (
    newValue: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>,
  ) => void
  value: MultiValue<OptionType>
  placeholder: string
  isLoading: boolean
  onMenuScrollToBottom: (event: WheelEvent | TouchEvent) => void
  inputError: string | undefined
  setParams: (params: BaseParams) => void
  data: OptionType[] | undefined
}

export function BasicAsyncSelect({
  handleSelectedValue,
  inputError,
  isLoading,
  onMenuScrollToBottom,
  placeholder,
  value,
  setParams,
  data,
}: BasicAsyncSelectProps) {
  const [options, setOptions] = useState<OptionType[]>([])
  const callbackRef = useRef<((options: OptionType[]) => void) | null>(null)
  const lastInputRef = useRef<string>('')
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null)

  useEffect(() => {
    const debouncedFetch = debounce((inputValue: string) => {
      if (inputValue.trim() !== '') {
        setParams({ search: inputValue })
      } else {
        setParams({ search: '' })
        if (callbackRef.current) {
          callbackRef.current([])
        }
      }
    }, 500)

    debouncedFetchRef.current = debouncedFetch

    return () => {
      debouncedFetchRef.current?.cancel()
    }
  }, [setParams])

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: OptionType[]) => void) => {
      lastInputRef.current = inputValue
      callbackRef.current = callback

      if (inputValue.trim() === '') {
        debouncedFetchRef.current?.cancel()
        callback([])
        setParams({ search: '' })
      } else {
        debouncedFetchRef.current?.(inputValue)
      }
    },
    [setParams],
  )

  const updateOptions = useCallback((newOptions: OptionType[]) => {
    setOptions(newOptions)
    if (callbackRef.current && lastInputRef.current.trim() !== '') {
      callbackRef.current(newOptions)
    }
  }, [])

  useEffect(() => {
    if (data) {
      updateOptions(data)
    }
  }, [data, updateOptions])

  const handleChange = (
    newValue: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>,
  ) => {
    handleSelectedValue(newValue, actionMeta)
    setParams({ search: '' })
    loadOptions('', () => {})
  }

  const { DropdownIndicator, customStyles } = FilterSelectStyle({
    isMulti: true,
  })

  const handleMenuScrollToBottom = useCallback(
    (event: WheelEvent | TouchEvent) => {
      if (!isLoading) {
        onMenuScrollToBottom(event)
      }
    },
    [isLoading, onMenuScrollToBottom],
  )

  return (
    <>
      <AsyncSelect
        isMulti
        isClearable
        onChange={handleChange}
        value={value}
        placeholder={placeholder}
        loadOptions={loadOptions}
        defaultOptions={options}
        styles={customStyles}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
        isLoading={isLoading}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(newValue, { action }) => {
          if (action === 'input-change') {
            loadOptions(newValue, () => {})
          }
        }}
      />
      <p className="mt-1 text-red-500">{inputError}</p>
    </>
  )
}
